import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { BenchmarkType, IndicatorName, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import { useChartZoom } from '@/components/Charts/components/ChartZoom';
import { GraphFact } from '@/components/Charts/types';
import { getChartDataWithBenchmarks } from '@/components/Charts/utils';
import { WaterHoldingCapacityChart } from '@/components/Charts/WaterHoldingCapacity';
import { GraphTileProps } from '@/components/IndicatorCard/GraphTile';
import { useNCCardContext } from '@/components/NCCard/NCCard';
import { useBenchmarksForOrganization } from '@/pages/shared/hooks/useBenchmarkForFact';
import {
  useOrganizationIndicatorGraphFact,
  useOrganizationIndicatorReportingFact,
} from '@/pages/shared/hooks/useOrganizationFact';

import { NoData } from '../../../components/NoData';

export const WaterGraphTile = (props?: GraphTileProps | null) => {
  const { t } = useTranslation();
  const { analysisType } = useNCCardContext();

  const { zoom, setZoom } = useChartZoom();
  const selectedZoom = props?.zoom ?? zoom; // props.zoom is used when this component is rendered as widget (e.g. measurement pages)
  const setZoomFunc = props?.setZoom ?? setZoom;
  const plotGroupId = props?.plotGroupId ?? null;

  const waterHoldingCapacity10yearUpliftPotentialFact =
    analysisType === 'total'
      ? IndicatorName.water_fc_uplift_potential_total_graph
      : IndicatorName.water_fc_uplift_potential_per_ha_graph;

  const { [waterHoldingCapacity10yearUpliftPotentialFact]: waterHoldingCapacity10yearUpliftPotential } =
    useOrganizationIndicatorGraphFact<GraphFact[]>(
      waterHoldingCapacity10yearUpliftPotentialFact,
      null,
      undefined,
      plotGroupId,
    );
  const whcData = waterHoldingCapacity10yearUpliftPotential?.value;

  const wiltingPoint10yearUpliftPotentialFact =
    analysisType === 'total'
      ? IndicatorName.below_wilting_point_uplift_potential_total_graph
      : IndicatorName.below_wilting_point_uplift_potential_per_ha_graph;
  const { [wiltingPoint10yearUpliftPotentialFact]: wiltingPoint10yearUpliftPotential } =
    useOrganizationIndicatorGraphFact<GraphFact[]>(wiltingPoint10yearUpliftPotentialFact, null, undefined, plotGroupId);
  const wpData = wiltingPoint10yearUpliftPotential?.value ?? [];

  const benchmarkFactType =
    analysisType === 'total' ? IndicatorName.water_fc_total_benchmark : IndicatorName.water_fc_per_ha_benchmark;

  const { [benchmarkFactType]: whcLowBenchmark } = useOrganizationIndicatorReportingFact<number>(
    benchmarkFactType,
    null,
    { [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low },
    plotGroupId,
  );

  const { [benchmarkFactType]: whcModerateBenchmark } = useOrganizationIndicatorReportingFact<number>(
    benchmarkFactType,
    null,
    { [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate },
    plotGroupId,
  );

  const { [benchmarkFactType]: whcHighBenchmark } = useOrganizationIndicatorReportingFact<number>(
    benchmarkFactType,
    null,
    { [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high },
    plotGroupId,
  );

  const whcBenchmarks = useBenchmarksForOrganization(benchmarkFactType, null, undefined, plotGroupId);

  /**
   * We only null check for whcData since whcData is the primary source data of the graph
   * wpData and other data are reference lines that are displayed only when whcData is rendered
   */
  const noChartData = !whcData?.length;
  if (noChartData) {
    return <NoData />;
  }

  const chartData = [
    ...whcData,
    ...wpData,
    ...getChartDataWithBenchmarks({
      chartData: whcData,
      benchmarks: { low: whcLowBenchmark, moderate: whcModerateBenchmark, high: whcHighBenchmark },
    }),
  ];

  return (
    <Stack center data-testid='water-uplift-graph-tile'>
      <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-6 lg:pl-1'>
        <ChartHeading
          // TODO: MVP-3284 rename the key
          heading={t('shared.ncaDetail.details.water.labels.upliftGraphHeadline_v1_2')}
          popover={{
            title: t('shared.ncaDetail.details.water.explainers.upliftPotential.title'),
            body: t('shared.ncaDetail.details.water.explainers.upliftPotential.body'),
          }}
          className='w-full px-6 sm:px-0'
          zoom={selectedZoom}
          setZoom={setZoomFunc}
        />
        <WaterHoldingCapacityChart
          analysisType={analysisType}
          data={chartData}
          benchmarks={whcBenchmarks}
          showTooltip={true}
          className='relative'
          data-testid='water-holding-capacity-chart'
          zoom={selectedZoom}
          setZoom={setZoomFunc}
        />
      </Stack>
    </Stack>
  );
};
